/**
 * OperatorProfile.jsx
 * 
 * Public page for viewing a marketplace operator's profile.
 * Reuses existing components from the marketplace for consistency.
 */

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { 
  Star,
  MapPin, 
  Globe,
  ChevronLeft,
  ExternalLink,
  Loader2,
  BookOpen
} from 'lucide-react';
import { ExperienceCard, ReviewCard } from '../components/marketplace';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) {
    const error = new Error(res.status === 404 ? 'Operator not found' : 'Failed to load operator');
    error.status = res.status;
    throw error;
  }
  return res.json();
};

const OperatorProfile = () => {
  const { operatorId } = useParams();
  const navigate = useNavigate();

  const { data: operator, error, isLoading } = useSWR(
    operatorId ? `${API_URL}/api/marketplace/operators/${operatorId}` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 30000 }
  );

  // Loading state - matches app style
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        {/* Same nav as ExperienceDetail */}
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-9 w-9 object-contain rounded-xl"
                />
                <span className="text-xl font-bold text-gray-900">BookingFlows</span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <Globe className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Operator not found</h1>
          <p className="text-gray-600 mb-6">This operator may no longer be available</p>
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to marketplace
          </button>
        </div>
      </div>
    );
  }

  if (!operator) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation - Same as ExperienceDetail */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="back-button"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-9 w-9 object-contain rounded-xl"
                />
                <span className="text-xl font-bold text-gray-900 hidden sm:inline">BookingFlows</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1">
        {/* Two Column Layout: Profile Left, Services Right */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column - Profile Info (sticky on desktop) */}
            <div className="lg:w-80 flex-shrink-0">
              <div 
                className="lg:sticky lg:top-24 bg-white rounded-3xl p-6"
                style={{ boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.1)' }}
              >
                {/* Logo */}
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gray-100 flex items-center justify-center mx-auto lg:mx-0 mb-4">
                  {operator.logoUrl ? (
                    <img 
                      src={operator.logoUrl} 
                      alt={operator.name} 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-3xl">{operator.name?.charAt(0)}</span>
                    </div>
                  )}
                </div>

                {/* Name & Badge */}
                <div className="text-center lg:text-left">
                  <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-2 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {operator.name}
                    </h1>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verified
                    </span>
                  </div>

                  {/* Rating - clickable to scroll to reviews */}
                  {operator.reviewCount > 0 && (
                    <button
                      onClick={() => document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' })}
                      className="flex items-center justify-center lg:justify-start gap-2 mb-3 hover:opacity-80 transition-opacity w-full"
                      data-testid="rating-scroll-btn"
                    >
                      <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                      <span className="font-semibold text-gray-900">{operator.averageRating}</span>
                      <span className="text-gray-500 text-sm">({operator.reviewCount} review{operator.reviewCount !== 1 ? 's' : ''})</span>
                    </button>
                  )}
                  
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 text-sm text-gray-600 mb-4">
                    {operator.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {operator.city}{operator.country ? `, ${operator.country}` : ''}
                      </span>
                    )}
                    <span className="text-gray-500">
                      {operator.serviceCount} experience{operator.serviceCount !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Bio */}
                  {operator.bio && (
                    <p className="text-gray-600 leading-relaxed mb-4 text-sm">
                      {operator.bio}
                    </p>
                  )}

                  {/* Website & Blog Links */}
                  <div className="flex flex-col gap-2 mt-3">
                    {operator.website && (
                      <a
                        href={operator.website.startsWith('http') ? operator.website : `https://${operator.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                        data-testid="operator-website-link"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Visit Website
                      </a>
                    )}

                    {/* Blog Link - only show if they have a published blog */}
                    {operator.hasBlog && operator.slug && (
                      <Link
                        to={`/blog/${operator.slug}`}
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                        data-testid="operator-blog-link"
                      >
                        <BookOpen className="w-4 h-4" />
                        Read Our Story
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Services */}
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Experiences
              </h2>
              
              {operator.services && operator.services.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {operator.services.map((service) => (
                    <ExperienceCard 
                      key={service.id}
                      service={{
                        ...service,
                        city: service.city || operator.city,
                        operator: {
                          name: operator.name,
                          rating: operator.averageRating,
                          reviewCount: operator.reviewCount
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <p className="text-gray-500">No experiences available at this time.</p>
                </div>
              )}

              {/* Reviews Section - Only show if operator has reviews */}
              {operator.reviewCount > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200" id="reviews-section" data-testid="reviews-section">
                  <div className="flex items-center gap-3 mb-6">
                    <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                    <span className="text-2xl font-bold text-gray-900">{operator.averageRating}</span>
                    <span className="text-gray-500">·</span>
                    <span className="text-gray-600">{operator.reviewCount} review{operator.reviewCount !== 1 ? 's' : ''}</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {operator.reviews.slice(0, 6).map((review) => (
                      <ReviewCard key={review.id} review={review} />
                    ))}
                  </div>

                  {operator.reviewCount > 6 && (
                    <button className="mt-6 text-sm font-medium text-gray-900 underline underline-offset-4 hover:text-blue-600 transition-colors">
                      Show all {operator.reviewCount} reviews
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer - Same as MarketplaceSearch */}
      <footer className="py-12 px-4 bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-xl"
              />
              <span className="text-lg font-bold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
              <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} BookingFlows
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OperatorProfile;
